/**
 * DigestSubjectMetricsCard — admin panel on /admin/email-config that shows
 * per-tier open/click rates for the mood-adaptive weekly-digest subject A/B.
 * Feeds off `GET /api/admin/digest/subject-metrics` which rolls up counts from
 * `digest_email_logs` using timestamps stamped by the Resend webhook.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Flame, Mail, AlertTriangle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  crushed_it:   { label: 'Crushed it',   color: '#34C759', example: 'You crushed it, {name}' },
  strong_week:  { label: 'Strong week',  color: '#007AFF', example: 'Strong week, {name}' },
  gentle_nudge: { label: 'Gentle nudge', color: '#FF9500', example: 'Gentle nudge for next week, {name}' },
  steady_week:  { label: 'Steady week',  color: '#AF52DE', example: 'Steady week, {name}' },
  busy_week:    { label: 'Busy week',    color: '#FF3B30', example: 'Busy week, {name} — your KStreak digest' },
  default:      { label: 'Default',      color: '#9CA3AF', example: 'Your KStreak weekly digest' },
};

const pct = (v) => v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`;

const DigestSubjectMetricsCard = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/digest/subject-metrics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  if (loading && !data) {
    return <Card className="border-[#E5E7EB]"><CardContent className="p-6 text-sm text-[#9CA3AF]">Loading digest subject metrics…</CardContent></Card>;
  }
  if (!data) return null;

  const totals = data.totals || {};
  const rows = data.tiers || [];

  return (
    <Card className="border-[#E5E7EB]" data-testid="digest-subject-metrics">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded bg-[#FF9500]/10 flex items-center justify-center flex-shrink-0">
              <Flame className="w-4 h-4 text-[#FF9500]" />
            </div>
            <div>
              <CardTitle className="text-sm">Adaptive digest subject — per-tier A/B</CardTitle>
              <p className="text-[11px] text-[#9CA3AF] mt-0.5">
                Each test digest is tagged with the tier that fired; Resend webhook events stamp delivery/open/click timestamps here.
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs flex-shrink-0" onClick={fetchMetrics} data-testid="refresh-subject-metrics-btn">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!data.webhook_configured && (
          <div className="flex items-start gap-2 p-2 bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded text-[11px] text-[#4B5563]">
            <AlertTriangle className="w-3 h-3 text-[#FF3B30] flex-shrink-0 mt-0.5" />
            <p>Resend webhook isn't configured — only <strong>sent</strong> counts will populate. Configure the webhook on the Email Health card above so delivery/open/click events flow in.</p>
          </div>
        )}

        {/* Totals strip */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-xs">
          <div className="p-2 border border-[#E5E7EB] rounded"><p className="text-[10px] text-[#9CA3AF]">Sent</p><p className="font-semibold">{totals.sent || 0}</p></div>
          <div className="p-2 border border-[#E5E7EB] rounded"><p className="text-[10px] text-[#9CA3AF]">Delivered</p><p className="font-semibold">{totals.delivered || 0}</p></div>
          <div className="p-2 border border-[#E5E7EB] rounded"><p className="text-[10px] text-[#9CA3AF]">Opened</p><p className="font-semibold">{totals.opened || 0}</p></div>
          <div className="p-2 border border-[#E5E7EB] rounded"><p className="text-[10px] text-[#9CA3AF]">Open rate</p><p className="font-semibold">{pct(totals.open_rate)}</p></div>
          <div className="p-2 border border-[#E5E7EB] rounded"><p className="text-[10px] text-[#9CA3AF]">Click rate</p><p className="font-semibold">{pct(totals.click_rate)}</p></div>
        </div>

        {/* Per-tier table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-[10px] text-[#9CA3AF] uppercase tracking-wide border-b border-[#E5E7EB]">
                <th className="text-left py-1.5 pr-2">Tier</th>
                <th className="text-right px-2">Sent</th>
                <th className="text-right px-2">Delivered</th>
                <th className="text-right px-2">Opened</th>
                <th className="text-right px-2">Clicked</th>
                <th className="text-right px-2">Bounced</th>
                <th className="text-left pl-2">Open rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const meta = TIER_META[row.tier] || TIER_META.default;
                const barWidth = row.open_rate ? Math.min(100, row.open_rate * 100) : 0;
                return (
                  <tr key={row.tier} className="border-b border-[#F3F4F6]" data-testid={`tier-row-${row.tier}`}>
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-2">
                        <Badge className="text-[9px] font-semibold" style={{ backgroundColor: meta.color + '15', color: meta.color }}>
                          {meta.label}
                        </Badge>
                        <span className="text-[10px] text-[#9CA3AF] italic hidden md:inline truncate max-w-[220px]">"{meta.example}"</span>
                      </div>
                    </td>
                    <td className="text-right px-2 font-mono">{row.sent}</td>
                    <td className="text-right px-2 font-mono">{row.delivered}</td>
                    <td className="text-right px-2 font-mono">{row.opened}</td>
                    <td className="text-right px-2 font-mono">{row.clicked}</td>
                    <td className="text-right px-2 font-mono">{row.bounced || 0}</td>
                    <td className="pl-2 min-w-[120px]">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden">
                          <div className="h-full transition-all" style={{ width: `${barWidth}%`, backgroundColor: meta.color }} />
                        </div>
                        <span className="text-[10px] font-semibold w-12 text-right">{pct(row.open_rate)}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-[10px] text-[#9CA3AF] flex items-center gap-1">
          <Mail className="w-3 h-3" />
          Rates exclude unsent rows. After a few weeks of data, drop any tier with an open rate &lt; the <em>Default</em> baseline.
        </p>
      </CardContent>
    </Card>
  );
};

export default DigestSubjectMetricsCard;
